/**
 * 60-second free audio preview gate (web redesign Phase 5).
 * Accumulates wall-clock play time; at 60s pauses and opens the paywall.
 * Premium + demo charts skip the gate.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useEntitlements } from "./EntitlementContext";
import { isDemoChart } from "./entitlements";

const PREVIEW_SEC = 60;

export function useAudioPreviewGate(opts: {
  chartId?: string | null;
  playing: boolean;
  onLimitReached: () => void;
}) {
  const { isPremium, openPaywall } = useEntitlements();
  const unlocked = isPremium || isDemoChart(opts.chartId);
  const [elapsed, setElapsed] = useState(0);
  const playedRef = useRef(0);
  const sessionStartRef = useRef<number | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (unlocked) {
      firedRef.current = false;
      return;
    }
    if (!opts.playing) {
      if (sessionStartRef.current != null) {
        playedRef.current += (Date.now() - sessionStartRef.current) / 1000;
        sessionStartRef.current = null;
        setElapsed(playedRef.current);
      }
      return;
    }

    sessionStartRef.current = Date.now();
    const id = window.setInterval(() => {
      const session =
        sessionStartRef.current != null
          ? (Date.now() - sessionStartRef.current) / 1000
          : 0;
      const total = playedRef.current + session;
      setElapsed(total);
      if (total >= PREVIEW_SEC && !firedRef.current) {
        firedRef.current = true;
        opts.onLimitReached();
        openPaywall(
          "60-second preview complete. Unlock full-length Life Music and Transit Radio.",
        );
      }
    }, 400);

    return () => {
      window.clearInterval(id);
      if (sessionStartRef.current != null) {
        playedRef.current += (Date.now() - sessionStartRef.current) / 1000;
        sessionStartRef.current = null;
      }
    };
    // onLimitReached intentionally stable via parent useCallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.playing, unlocked, openPaywall]);

  const remaining = Math.max(0, PREVIEW_SEC - elapsed);
  const limited = !unlocked && elapsed >= PREVIEW_SEC;

  const reset = useCallback(() => {
    playedRef.current = 0;
    sessionStartRef.current = null;
    firedRef.current = false;
    setElapsed(0);
  }, []);

  return {
    unlocked,
    elapsed,
    remaining,
    limited,
    previewSec: PREVIEW_SEC,
    reset,
  };
}
